import React, { Fragment, useState, useEffect, useCallback } from "react";
import { push } from "connected-react-router";
import moment from "moment";
import { Button } from "reactstrap";
import { useSelector, useDispatch } from "react-redux";
import MUIDataTable from "mui-datatables";

import getQueryString from "./utils.js";

import { fetchOffers, cancelOffers } from "../../actions/offer";
import CustomToolbar, { CustomToolbarSelect } from "./components/CustomToolbar.js";

const Offers = (props) => {
  const dispatch = useDispatch();
  const store = useSelector((state) => state.offers);

  const [dataOptions, setDataOptions] = useState("");
  const [sortColumns, setSortColumns] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);

  const refetchData = useCallback(
    (dataOptions) => {
      dispatch(fetchOffers(dataOptions));
    },
    [dispatch]
  );

  useEffect(() => {
    refetchData(dataOptions);
  }, [dataOptions, refetchData]);

  const handleAddItem = () => {
    dispatch(push(`/resources/offers/add`));
  };

  const handleCancelOffers = () => {
    if (selectedRows.length === 0) return;

    // Get selected offer data
    const offersToCancel = selectedRows.map((rowIndex) => {
      const offer = store.offers[rowIndex.index];
      return {
        facility_name: offer.facility_name,
        facility_id: offer.facility_id,
        offer_date: offer.offer_date,
        period: offer.period,
      };
    });

    // A cancel is only allowed when the current time is more than 65 minutes
    // before the start of the offer's period.
    const now = moment();
    const tooSoon = offersToCancel.filter((offer) => {
      const periodStart = moment(offer.offer_date).startOf('day').add(
        (offer.period - 1) * 30,
        "minutes"
      );
      return now.isSameOrAfter(periodStart.clone().subtract(65, "minutes"));
    });
    if (tooSoon.length > 0) {
      const details = tooSoon
        .map(
          (offer) =>
            `${offer.facility_name} ${moment(offer.offer_date).format("DD MMM YYYY")} P${offer.period}`
        )
        .join("\n");
      window.alert(
        `Gate Closure for the following offer(s) has passed:\n\n${details}`
      );
      return;
    }

    // Show confirmation dialog
    if (!window.confirm("Are you sure you want to cancel these offers?")) {
      return;
    }

    dispatch(cancelOffers(offersToCancel));
    setSelectedRows([]);
  };

  const columns = [
    // {
    //   label: "OFFER ID",
    //   name: 'id',
    //   options: {
    //     filter: false,
    //   }
    // },
    {
      label: "FACILITY",
      name: "facility_name",
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: "offer_date",
      label: "OFFER DATE",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value, tableMeta, updateValue) => {
          return moment(value, "YYYY-MM-DDTHH:mm:ss.SSSZ").format(
            "DD MMM YYYY"
          );
        },
      },
    },
    {
      label: "PERIOD",
      name: "period",
      options: {
        filter: false,
        sort: true,
      },
    },
    // {
    //   name: "emc_offer_batch_number",
    //   label: "OFFER BATCH NUMBER",
    //   options: {
    //     filter: false
    //   }
    // },
    {
      name: "offer",
      label: "OFFER OBJECT",
      options: {
        filter: false,
        customBodyRender: (value, tableMeta, updateValue) => {
          let returnStrings = [];
          if (value) {
            for (let index = 0; index < value.length; index++) {
              const element = value[index];
              if (index === 0 || element.q > 0) {
                returnStrings.push(`• ${element.q}MW for ${element.p}$/MWh`);
              }
            }
          }

          return (
            <div>
              {returnStrings.map((sentence, index) => (
                <div key={index}>{sentence}</div>
              ))}
            </div>
          );
        },
      },
    },
    {
      name: "offer_status",
      label: "STATUS",
      options: {
        filter: true,
        sort: true,
        filterType: "checkbox",
        filterOptions: {
          names: [
            "pending",
            "validated",
            "accepted",
            "approved",
            "rejected",
            "not-accepted",
            "cleared",
            "not-cleared",
          ],
        },
        customFilterListOptions: {
          render: (v) => {
            if (v.length > 0) {
              return `STATUS: ${v}`;
            }
            return [];
          },
          update: (filterList, filterPos, index) => {
            if (filterPos === 0) {
              filterList[index].splice(filterPos, 1, "");
            } else if (filterPos === 1) {
              filterList[index].splice(filterPos, 1);
            } else if (filterPos === -1) {
              filterList[index] = [];
            }

            return filterList;
          },
        },
      },
    },
    {
      label: "CLEARED LOAD",
      name: "taken_amount",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value, tableMeta, updateValue) => {
          return value && value > 0 ? value : "-";
        },
      },
    },
    {
      name: "ACTIONS",
      options: {
        filter: false,
        sort: false,
        empty: true,
        customBodyRenderLite: (dataIndex, rowIndex) => {
          return (
            <div className="d-flex justify-content">
              {/* Cancel button replaces Delete - shows when item is selected via row selection */}
            </div>
          );
        },
      },
    },
  ];

  const options = {
    search: false,
    filterType: "checkbox",
    count: store.total_offers,
    serverSide: true,
    rowsPerPage: 20,
    rowsPerPageOptions: [20, 50, 100],
    responsive: "vertical",
    selectableRows: "multiple",
    selectableRowsOnClick: false,
    // Note: Not using rowsSelected here because it can interfere with
    // the table's internal selection state management
    onRowSelectionChange: (rowsData, rowsSelected) => {
      // Update our state with the currently selected row indices
      setSelectedRows(rowsSelected || []);
    },
    onTableChange: (action, tableState) => {
      switch (action) {
        case "changePage":
          setDataOptions(getQueryString(tableState, sortColumns, false, "title_first"));
          break;
        case "sort":
          if (
            sortColumns.filter(
              (sort) => sort.name === tableState.sortOrder.name
            ).length > 0
          ) {
            // update the existing sortColumn
            let index = sortColumns.findIndex(
              (sort) => sort.name === tableState.sortOrder.name
            );
            sortColumns[index] = {
              name: tableState.sortOrder.name,
              direction: tableState.sortOrder.direction,
            };

            setSortColumns(sortColumns);
          } else {
            // add a new sortColumn
            sortColumns.push({
              name: tableState.sortOrder.name,
              direction: tableState.sortOrder.direction,
            });

            setSortColumns(sortColumns);
          }

          setDataOptions(getQueryString(tableState, sortColumns, false, "title_first"));
          break;
        case "filterChange":
          console.log(tableState, sortColumns);
          setDataOptions(getQueryString(tableState, sortColumns, false, "title_first"));
          break;
        case "changeRowsPerPage":
          setDataOptions(getQueryString(tableState, sortColumns, false, "title_first"));
          break;
        default:
          break;
      }
    },
    customToolbar: () => {
      return <CustomToolbar action={handleAddItem} />;
    },
    customToolbarSelect: () => {
      return <CustomToolbarSelect selectedRowsCount={selectedRows.length} cancelAction={handleCancelOffers} />;
    },
  };

  return (
    <Fragment>
      <div>
        <style>{`
          @media (max-width: 768px) {
            [class*="MUIDataTableBodyCell-root-"] {
              width: calc(100%) !important;
            }
            [class*="MUIDataTableBodyCell-stackedHeader-"] {
              font-size: 10px !important;
            }
          }
        `}</style>
        <MUIDataTable
          title={`Offers ${
            sortColumns.length > 0
              ? " (sorted by : " +
                sortColumns.map(
                  (col) => " " + col.name + " " + col.direction + " "
                ) +
                ")"
              : ""
          }`}
          data={store.offers}
          columns={columns}
          options={options}
        />
      </div>
    </Fragment>
  );
};

export default Offers;
